import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import moment from 'moment';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';

const UPDATE_PREVIEW_LENGTH = 800;

const UPDATE_HTML_OPTIONS = {
  ALLOWED_TAGS: [
    'a',
    'b',
    'blockquote',
    'br',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'i',
    'li',
    'ol',
    'p',
    'span',
    'strong',
    'u',
    'ul',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
};

const normalizeUpdateContent = (content) =>
  String(content || '')
    .replace(/\\r\\n|\\n|\\r/g, '\n')
    .replace(/\r\n|\r/g, '\n');

const truncateHtmlByTextLength = (html, maxLength) => {
  const source = document.createElement('div');
  const output = document.createElement('div');
  source.innerHTML = html;

  let remaining = maxLength;
  let truncated = false;

  const copyChildren = (sourceParent, outputParent) => {
    for (const node of Array.from(sourceParent.childNodes)) {
      if (remaining === 0) {
        truncated = true;
        return;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const characters = Array.from(node.textContent || '');

        if (characters.length <= remaining) {
          outputParent.appendChild(node.cloneNode(true));
          remaining -= characters.length;
        } else {
          outputParent.appendChild(
            document.createTextNode(characters.slice(0, remaining).join(''))
          );
          remaining = 0;
          truncated = true;
          return;
        }

        continue;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const clone = node.cloneNode(false);
        outputParent.appendChild(clone);
        copyChildren(node, clone);

        if (truncated) return;
      }
    }
  };

  copyChildren(source, output);

  return {
    html: output.innerHTML,
    truncated,
  };
};

function SingleUpdate({ title, time, content, image, images }) {

  const normalizedImages =
    Array.isArray(images) && images.length > 0 ? images : image ? [image] : [];

  const [selectedImage, setSelectedImage] = useState(null);
  const [showFullContent, setShowFullContent] = useState(false);
  const contentToggleRef = useRef(null);
  const scrollAfterCollapseRef = useRef(false);

  const updateContent = useMemo(() => {
    const sanitizedHtml = DOMPurify.sanitize(
      normalizeUpdateContent(content),
      UPDATE_HTML_OPTIONS
    );
    const preview = truncateHtmlByTextLength(
      sanitizedHtml,
      UPDATE_PREVIEW_LENGTH
    );

    return {
      fullHtml: sanitizedHtml,
      previewHtml: preview.html,
      isLong: preview.truncated,
    };
  }, [content]);

  const handleOpenImage = (img) => setSelectedImage(img);
  const handleCloseImage = () => setSelectedImage(null);
  const handleContentToggle = () => {
    if (showFullContent) {
      scrollAfterCollapseRef.current = true;
    }

    setShowFullContent((current) => !current);
  };

  useEffect(() => {
    if (showFullContent || !scrollAfterCollapseRef.current) return undefined;

    scrollAfterCollapseRef.current = false;
    const animationFrame = window.requestAnimationFrame(() => {
      const prefersReducedMotion = window.matchMedia?.(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      contentToggleRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [showFullContent]);

  useEffect(() => {
    if (!selectedImage) return undefined;

    const prevOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleCloseImage();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  return (
    <SingleTimeWrapper>
      {selectedImage
        ? createPortal(
          <ImageLightbox
            role="dialog"
            aria-modal="true"
            aria-label="Bildvorschau"
            onClick={handleCloseImage}
          >
            <CloseButton
              type="button"
              onClick={handleCloseImage}
              aria-label="Bildvorschau schließen"
              autoFocus
            >
              <span aria-hidden="true">×</span>
            </CloseButton>
            <ImageLightboxInner>
              <FullSizeImage
                src={selectedImage}
                alt={title || 'project-updated-img'}
                onClick={(event) => event.stopPropagation()}
              />
            </ImageLightboxInner>
          </ImageLightbox>,
          document.body
        )
        : null}

      <Title>
        <span>{title}</span>
        <div>
          veröffentlicht am {moment.unix(Number(time)).format('DD.MM.YYYY')}
        </div>
      </Title>

      <Content>
        <UpdateBody
          dangerouslySetInnerHTML={{
            __html: showFullContent
              ? updateContent.fullHtml
              : updateContent.previewHtml,
          }}
        />

        {updateContent.isLong && (
          <ContentToggle
            ref={contentToggleRef}
            type="button"
            aria-expanded={showFullContent}
            onClick={handleContentToggle}
          >
            {showFullContent ? 'Weniger anzeigen' : 'Mehr anzeigen'}
          </ContentToggle>
        )}

        {normalizedImages.length > 0 ? (
          <ImagesGrid>
            {normalizedImages.map((img, index) => (
              <TimelineImage
                key={`${img}-${index}`}
                src={img}
                alt={`project-updated-img-${index + 1}`}
                onClick={() => handleOpenImage(img)}
              />
            ))}
          </ImagesGrid>
        ) : null}
      </Content>
    </SingleTimeWrapper>
  );
}

SingleUpdate.propTypes = {
  title: PropTypes.string,
  time: PropTypes.number.isRequired,
  content: PropTypes.string.isRequired,
  image: PropTypes.any,
  images: PropTypes.arrayOf(PropTypes.string),
};


const SingleTimeWrapper = styled.div``;

const Title = styled.div`
  position: relative;

  span {
    /* Heading */

    font-family: 'Inter';
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 30px;
    color: #181d27;
    @media (max-width: 768px) {
      font-size: 18px;
    }
  }

  div {
  
    /* Text sm/Regular */
    font-family: 'Inter';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    /* identical to box height, or 143% */

    color: #535862;
    
    @media (max-width: 768px) {
      font-size: 12px;
    }
  }
`;

const Content = styled.div`
  position: relative;
  font-size: 16px;
  letter-spacing: 0%;
  font-weight: 400;
  font-family: Inter, sans-serif;
  color: #535862;
  margin-top: 8px;
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const UpdateBody = styled.div`
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #535862;

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0 0 12px;
  }

  ul,
  ol,
  blockquote {
    margin-top: 0;
    margin-bottom: 12px;
  }

  a {
    color: #9dbc60;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const ContentToggle = styled.button`
  display: block;
  margin: 8px 0 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #9dbc60;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const TimelineImage = styled.img`
  display: block;

  width: 182px;

  height: 121.33px;

  object-fit: cover;

  margin-bottom: 0;

  border-radius: 8px;

  cursor: pointer;

  @media (max-width: 768px) {
    width: calc((100% - 24px) / 3);

    height: auto;

    aspect-ratio: 106.33 / 70.89;

    margin-bottom: 0;

    flex: 0 0 calc((100% - 24px) / 3);
  }
`;

const ImagesGrid = styled.div`
  display: flex;

  flex-wrap: wrap;

  gap: 12px;

  margin-top: 12px;

  @media (max-width: 768px) {
    width: 100%;

    max-width: 343px;

    flex-wrap: wrap;

    align-items: center;

    gap: 12px;
  }
`;
const ImageLightbox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.75);
`;

const ImageLightboxInner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(96vw, 1200px);
  height: min(92vh, 900px);
  padding: 24px;
  box-sizing: border-box;
`;

const CloseButton = styled.button`
  position: fixed;
  top: max(16px, env(safe-area-inset-top));
  right: max(16px, env(safe-area-inset-right));
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.96);
  color: #181d27;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.24);

  &:focus-visible {
    outline: 3px solid #9dbc60;
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    width: 52px;
    height: 52px;
    font-size: 34px;
  }
`;

const FullSizeImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
  height: auto;
  width: auto;
  border-radius: 12px;
  object-fit: contain;
`;


export default SingleUpdate;
